"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { wholeWordPartSource } from "@/lib/tool-kit/text/whole-word";

/* ------------------------------------------------------------------ */
/*  Stop-word list + module-level regex helpers                       */
/* ------------------------------------------------------------------ */

const STOP_WORDS: string[] = [
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "nor",
  "so",
  "yet",
  "of",
  "to",
  "in",
  "on",
  "at",
  "by",
  "for",
  "with",
  "from",
  "up",
  "down",
  "out",
  "off",
  "over",
  "under",
  "into",
  "onto",
  "about",
  "as",
  "is",
  "am",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "do",
  "does",
  "did",
  "have",
  "has",
  "had",
  "will",
  "would",
  "shall",
  "should",
  "can",
  "could",
  "may",
  "might",
  "must",
  "it",
  "its",
  "this",
  "that",
  "these",
  "those",
  "there",
  "then",
  "than",
  "not",
];

const RE_ESCAPE = /[.*+?^${}()|[\]\\]/g;
const RE_COMMA = /,/;
const RE_SPLIT_INPUT_WORDS = /[\s,]+/;
const RE_SPLIT_WORDS = /\s+/;
const RE_LEFTOVER_SPACES = /[^\S\n]{2,}/g;
/** Marks the spot a word was cut from, so only those gaps are closed up. */
const REMOVED = "\uE000";
const RE_REMOVED_GAP = /[^\S\n]*(?:\uE000[^\S\n]*)+/g;
const RE_GAP_CLOSER = /[.,;:!?\n]/;
const RE_LINE_START_SPACE = /^[^\S\n]+/gm;
const RE_SENTENCE_END = /(?:[.!?…]["'”’)\]]*|\n)\s*$/;
const RE_LEADING_LOWER = /^(\s*)(\p{Ll})/u;

/** English strings. A translated content file overrides these through `ui`. */
export const REMOVE_WORDS_STRINGS = {
  inputText: "Input text",
  inputPlaceholder: "Paste the text you want to remove words from...",
  wordsToRemove: "Words to remove — separated by commas or spaces",
  wordsPlaceholder: "e.g. very, really, just",
  useStopWordsLabel:
    "Also remove common stop words (the, a, an, and, of, to, in, is...)",
  removeWords: "Remove Words",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  result: "Result",
  wordsRemoved: "Words Removed",
  wordsRemaining: "Words Remaining",
} as const;

function escapeRegExp(value: string): string {
  return value.replace(RE_ESCAPE, "\\$&");
}

/* ------------------------------------------------------------------ */
/*  Removal logic                                                     */
/* ------------------------------------------------------------------ */

function parseWordList(raw: string): string[] {
  if (RE_COMMA.test(raw)) {
    return raw
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return raw.split(RE_SPLIT_WORDS).filter(Boolean);
}

interface RemovalResult {
  output: string;
  removed: number;
}

/**
 * Does a sentence begin where the text before `offset` ends? True at the start of the
 * text, after a line break, and after . ! ? … (with any closing quote or bracket).
 */
function startsSentence(text: string, offset: number): boolean {
  if (offset === 0) {
    return true;
  }
  const before = text.slice(Math.max(0, offset - 32), offset);
  if (offset <= 32 && before.trim().length === 0) {
    return true;
  }
  return RE_SENTENCE_END.test(before);
}

/** Upper-case the first letter of `segment` when only whitespace precedes it. */
function capitaliseFirstLetter(segment: string): string {
  return segment.replace(
    RE_LEADING_LOWER,
    (_m, space: string, letter: string) => space + letter.toLocaleUpperCase()
  );
}

export function removeWords(
  text: string,
  rawWords: string,
  useStopWords: boolean
): RemovalResult {
  const words = new Set(
    parseWordList(rawWords).map((word) => word.toLowerCase())
  );
  if (useStopWords) {
    for (const word of STOP_WORDS) {
      words.add(word);
    }
  }
  if (words.size === 0) {
    return { output: text, removed: 0 };
  }

  const pattern = Array.from(words)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  const regex = new RegExp(wholeWordPartSource(pattern), "giu");

  let removed = 0;
  let stripped = "";
  let last = 0;
  let capitalise = false;
  for (const match of text.matchAll(regex)) {
    const between = text.slice(last, match.index);
    stripped +=
      (capitalise ? capitaliseFirstLetter(between) : between) + REMOVED;
    // A sentence opener that disappears hands its capital to the word after it; the
    // flag survives a run of removed openers ("The a cat") but not a kept word.
    capitalise =
      (capitalise && between.trim().length === 0) ||
      startsSentence(text, match.index);
    last = match.index + match[0].length;
    removed += 1;
  }
  const tail = text.slice(last);
  stripped += capitalise ? capitaliseFirstLetter(tail) : tail;
  // A gap left by a removed word becomes one space, or nothing at a line edge or before
  // punctuation. Spaces the writer typed elsewhere, such as the French space before
  // ; : ! ?, are left alone.
  const output = stripped
    .replace(RE_REMOVED_GAP, (gap, offset: number, whole: string) => {
      const before = whole[offset - 1];
      const after = whole[offset + gap.length];
      if (
        before === undefined ||
        before === "\n" ||
        after === undefined ||
        RE_GAP_CLOSER.test(after)
      ) {
        return "";
      }
      return " ";
    })
    .replace(RE_LEFTOVER_SPACES, " ")
    .replace(RE_LINE_START_SPACE, "");
  return { output, removed };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Widget Component                                                  */
/* ------------------------------------------------------------------ */

export default function RemoveWordsWidget() {
  const strings = useToolUi(REMOVE_WORDS_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [wordList, setWordList] = useState("");
  const [useStopWords, setUseStopWords] = useState(false);
  const [result, setResult] = useState<RemovalResult | null>(null);
  const [copied, setCopied] = useState(false);

  const canRemove =
    input.trim().length > 0 && (wordList.trim().length > 0 || useStopWords);

  function handleRemove() {
    if (!canRemove) {
      return;
    }
    setResult(removeWords(input, wordList, useStopWords));
    setCopied(false);
  }

  function handleCopy() {
    if (!result) {
      return;
    }
    writeToClipboard(result.output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setWordList("");
    setResult(null);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="remove-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="remove-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Words to remove */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="remove-words"
        >
          {strings.wordsToRemove}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="remove-words"
          onChange={(e) => setWordList(e.target.value)}
          placeholder={strings.wordsPlaceholder}
          type="text"
          value={wordList}
        />
        <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
          <input
            checked={useStopWords}
            className="accent-primary"
            onChange={(e) => setUseStopWords(e.target.checked)}
            type="checkbox"
          />
          {strings.useStopWordsLabel}
        </label>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!canRemove}
          onClick={handleRemove}
          type="button"
        >
          {strings.removeWords}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={!result}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && !result}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {result && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="remove-output"
          >
            {strings.result}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="remove-output"
            readOnly
            value={result.output}
          />
        </div>
      )}

      {/* Stats */}
      {result && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard label={strings.wordsRemoved} value={fmt(result.removed)} />
          <StatCard
            label={strings.wordsRemaining}
            value={
              result.output.trim().length === 0
                ? 0
                : fmt(result.output.trim().split(RE_SPLIT_INPUT_WORDS).length)
            }
          />
        </div>
      )}
    </>
  );
}
